import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const api = axios.create({
  withCredentials: true,
  baseURL: API_BASE_URL,
  timeout: 6000000,
});

// API service functions
export const apiService = {
  // Generic GET method
  get: async (endpoint) => {
    try {
      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      console.error(`Error getting ${endpoint}:`, error);
      throw error;
    }
  },
  validateInvitationToken: async (token, email) => {
    try {
      const response = await api.get(
        `/api/v2/auth/invitations/validate/${token}?email=${encodeURIComponent(
          email,
        )}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error validating invitation token:", error);
      throw error;
    }
  },

  /**
   * Accept invitation and create account
   * @param {Object} data - Invitation acceptance data
   * @param {string} data.token - Invitation token
   * @param {string} data.email - User email
   * @param {string} data.firstName - First name
   * @param {string} data.lastName - Last name
   * @param {string} data.password - Password
   * @param {string} data.confirmPassword - Confirm password
   * @returns {Promise<Object>} Acceptance result
   */
  acceptInvitation: async (data) => {
    try {
      const response = await api.post("/api/v2/auth/invitations/accept", data);
      return response.data;
    } catch (error) {
      console.error("Error accepting invitation:", error);
      throw error;
    }
  },

  sendEmails: async (group) => {
    try {
      const response = await api.post("/api/top5-rfps/send-emails", { group });
      return response.data;
    } catch (error) {
      console.error("Error sending emails:", error);
      throw error;
    }
  },

  // Get today's RFPs list
  getTodayRfpsList: async (params = {}) => {
    try {
      // Create a copy of params to avoid mutating the original
      const apiParams = { ...params };

      // Map frontend field names to backend field names
      const fieldMappings = {
        created_at: "createdAt",
        response_date: "responseDate",
        total_votes: "totalVotes",
        positive_votes: "positiveVotes",
        group_score: "groupScore",
      };

      // Convert field names if needed
      if (apiParams.sortBy && fieldMappings[apiParams.sortBy]) {
        apiParams.sortBy = fieldMappings[apiParams.sortBy];
      }

      // Convert filterType to match backend expectations
      if (apiParams.filterType) {
        // Ensure filterType is in the correct format
        const filterTypeMapping = {
          TODAY_ALL: "today_all",
          TODAY_NO_GROUPS: "today_no_groups",
          VOTED_ON: "voted_on",
          TOP_SCORED: "top_scored",
          MISSED: "missed",
          SHARED_WITH_ME: "shared_with_me",
          ARCHIVED: "archived", // ADD THIS LINE
        };

        if (filterTypeMapping[apiParams.filterType]) {
          apiParams.filterType = filterTypeMapping[apiParams.filterType];
        }
      }

      const queryParams = new URLSearchParams();
      Object.entries(apiParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Convert boolean values to strings
          if (typeof value === "boolean") {
            queryParams.append(key, value.toString());
          } else {
            queryParams.append(key, value);
          }
        }
      });

      const queryString = queryParams.toString();
      const url = queryString
        ? `/api/top5-rfps/today-rfps-list?${queryString}`
        : "/api/top5-rfps/today-rfps-list";

      console.log("Fetching RFPs with params:", apiParams); // Debug log

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("Error getting today's RFPs:", error);
      return {
        success: false,
        rfps: [],
        statistics: {},
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          limit: params.limit || 25,
          hasNextPage: false,
          hasPrevPage: false,
        },
      };
    }
  },

  // ==================== CHAT API METHODS ====================

  /**
   * Get or create a chat for a specific opportunity
   * @param {number} opportunityId - The ID of the opportunity/RFP
   * @returns {Promise<Object>} Chat data
   */

  async getOrCreateChat(opportunityId, params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.groupId) {
        queryParams.append("groupId", params.groupId);
      }
      if (params.documentId) {
        queryParams.append("documentId", params.documentId);
      }

      const queryString = queryParams.toString();
      const url = `/api/chats/opportunity/${opportunityId}${queryString ? `?${queryString}` : ""}`;

      console.log("🔍 Chat API URL:", url); // Debug log
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("Error getting/creating chat:", error);
      throw error;
    }
  },

  /**
   * Get all chats for the current user's company
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 20)
   * @returns {Promise<Object>} Paginated chats data
   */
  getCompanyChats: async (page = 1, limit = 20) => {
    try {
      const response = await api.get(`/api/chats?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching company chats:", error);
      throw error;
    }
  },

  /**
   * Get messages for a specific chat
   * @param {string} chatId - The ID of the chat
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Messages per page (default: 50)
   * @returns {Promise<Object>} Paginated messages data
   */
  getChatMessages: async (chatId, page = 1, limit = 50) => {
    try {
      const response = await api.get(
        `/api/chats/${chatId}/messages?page=${page}&limit=${limit}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching chat messages:", error);
      throw error;
    }
  },

  /**
   * Send a new message in a chat
   * @param {string} chatId - The ID of the chat
   * @param {Object} data - Message data
   * @param {string} data.content - Message content
   * @param {string} data.messageType - Message type ('TEXT' or 'SYSTEM', default: 'TEXT')
   * @returns {Promise<Object>} Created message data
   */
  sendChatMessage: async (chatId, data) => {
    try {
      const response = await api.post(`/api/chats/${chatId}/messages`, data);
      return response.data;
    } catch (error) {
      console.error("Error sending chat message:", error);
      throw error;
    }
  },

  /**
   * Edit an existing message
   * @param {string} chatId - The ID of the chat
   * @param {string} messageId - The ID of the message to edit
   * @param {string} content - New message content
   * @returns {Promise<Object>} Updated message data
   */
  editChatMessage: async (chatId, messageId, content) => {
    try {
      const response = await api.put(
        `/api/chats/${chatId}/messages/${messageId}`,
        {
          content,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error editing chat message:", error);
      throw error;
    }
  },

  /**
   * Delete a message (soft delete)
   * @param {string} chatId - The ID of the chat
   * @param {string} messageId - The ID of the message to delete
   * @returns {Promise<Object>} Deletion result
   */
  deleteChatMessage: async (chatId, messageId) => {
    try {
      const response = await api.delete(
        `/api/chats/${chatId}/messages/${messageId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting chat message:", error);
      throw error;
    }
  },

  /**
   * Search for chats by opportunity title or ID
   * @param {string} query - Search query
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 20)
   * @returns {Promise<Object>} Paginated search results
   */
  searchChats: async (query, page = 1, limit = 20) => {
    try {
      const response = await api.get(
        `/api/chats/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error searching chats:", error);
      throw error;
    }
  },

  // ==================== AI CHAT API METHODS ====================

  /**
   * Send a message to the AI chatbot
   * @param {Object} data - Message data
   * @param {string} data.user_id - User ID (required)
   * @param {string} data.sam_opportunity_id - Opportunity ID (optional)
   * @param {string} data.message - User's message (required)
   * @param {string} data.page_context - Page context like "document:123" (optional)
   * @returns {Promise<Object>} AI response with answer and sources
   */
  sendAIChatMessage: async (data) => {
    try {
      const response = await api.post("/api/aichat/message", data);
      return response.data;
    } catch (error) {
      console.error("Error sending AI chat message:", error);
      throw error;
    }
  },

  /**
   * Get AI chat conversation history
   * @param {Object} data - History request data
   * @param {string} data.user_id - User ID (required)
   * @param {string} data.sam_opportunity_id - Opportunity ID (optional)
   * @param {string} data.page_context - Page context (optional)
   * @returns {Promise<Object>} Conversation history with messages
   */
  getAIChatHistory: async (data) => {
    try {
      const response = await api.post("/api/aichat/history", data);
      return response.data;
    } catch (error) {
      console.error("Error getting AI chat history:", error);
      throw error;
    }
  },

  /**
   * Clear AI chat conversation
   * @param {Object} data - Clear request data
   * @param {string} data.user_id - User ID (required)
   * @param {string} data.sam_opportunity_id - Opportunity ID (optional)
   * @param {string} data.page_context - Page context (optional)
   * @returns {Promise<Object>} Clear result with deleted count
   */
  clearAIChat: async (data) => {
    try {
      const response = await api.post("/api/aichat/clear", data);
      return response.data;
    } catch (error) {
      console.error("Error clearing AI chat:", error);
      throw error;
    }
  },

  // ==================== GROUP AI CHAT API METHODS ====================

  /**
   * Send a message to the group AI chatbot
   * @param {Object} data - Group chat message data
   * @param {string} data.user_id - User ID (required)
   * @param {number} data.relevant_document_id - Relevant document ID (required)
   * @param {string} data.message - User's message (required)
   * @returns {Promise<Object>} AI response with answer and sources
   */
  sendGroupAIChatMessage: async (data) => {
    try {
      const response = await api.post("/api/aichat/group/message", data);
      return response.data;
    } catch (error) {
      console.error("Error sending group AI chat message:", error);
      throw error;
    }
  },

  /**
   * Get group AI chat conversation history
   * @param {Object} data - Group chat history request data
   * @param {string} data.user_id - User ID (required)
   * @param {number} data.relevant_document_id - Relevant document ID (required)
   * @returns {Promise<Object>} Conversation history with messages
   */
  getGroupAIChatHistory: async (data) => {
    try {
      const response = await api.post("/api/aichat/group/history", data);
      return response.data;
    } catch (error) {
      console.error("Error getting group AI chat history:", error);
      throw error;
    }
  },

  /**
   * Clear group AI chat conversation
   * @param {Object} data - Group chat clear request data
   * @param {string} data.user_id - User ID (required)
   * @param {number} data.relevant_document_id - Relevant document ID (required)
   * @returns {Promise<Object>} Clear result with deleted count
   */
  clearGroupAIChat: async (data) => {
    try {
      const response = await api.post("/api/aichat/group/clear", data);
      return response.data;
    } catch (error) {
      console.error("Error clearing group AI chat:", error);
      throw error;
    }
  },
  // ================= DECISION METHODS =================

  /**
   * Get decision for a specific opportunity
   * @param {string|number} opportunityId - The opportunity ID
   * @returns {Promise<Object>} Decision data
   */
  getDecision: async (opportunityId) => {
    try {
      const response = await api.get(`/api/decisions/${opportunityId}`);
      return response.data;
    } catch (error) {
      console.error(
        `Error getting decision for opportunity ${opportunityId}:`,
        error,
      );
      // Return null if decision doesn't exist (404)
      if (error.response?.status === 404) {
        return { success: true, data: null };
      }
      throw error;
    }
  },

  /**
   * Create or update decision for an opportunity
   * @param {string|number} opportunityId - The opportunity ID
   * @param {Object} data - Decision data
   * @param {string} data.decision - Decision text (required)
   * @param {string} data.notes - Optional notes
   * @returns {Promise<Object>} Updated decision data
   */
  updateDecision: async (opportunityId, data) => {
    try {
      const response = await api.post(`/api/decisions/${opportunityId}`, data);
      return response.data;
    } catch (error) {
      console.error(
        `Error updating decision for opportunity ${opportunityId}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Delete decision for an opportunity
   * @param {string|number} opportunityId - The opportunity ID
   * @returns {Promise<Object>} Deletion result
   */
  deleteDecision: async (opportunityId) => {
    try {
      const response = await api.delete(`/api/decisions/${opportunityId}`);
      return response.data;
    } catch (error) {
      console.error(
        `Error deleting decision for opportunity ${opportunityId}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Get full history for a decision
   * @param {string|number} opportunityId - The opportunity ID
   * @returns {Promise<Object>} Decision history
   */
  getDecisionHistory: async (opportunityId) => {
    try {
      const response = await api.get(`/api/decisions/${opportunityId}/history`);
      return response.data;
    } catch (error) {
      console.error(
        `Error getting decision history for opportunity ${opportunityId}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Get all decisions for the company (with pagination and search)
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.search - Search term
   * @returns {Promise<Object>} Company decisions with pagination
   */
  getCompanyDecisions: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const url = queryString
        ? `/api/decisions?${queryString}`
        : "/api/decisions";

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("Error getting company decisions:", error);
      throw error;
    }
  },
  /**
   * Get decision reports with filtering and pagination
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Decision reports data with pagination
   */
  getDecisionReports: async (params) => {
    try {
      const queryParams = new URLSearchParams();

      // Add all params to query string
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          queryParams.append(key, String(value));
        }
      });

      const response = await api.get(
        `/api/feedback/reports?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching decision reports:", error);
      return {
        success: false,
        data: [],
        pagination: null,
        stats: null,
      };
    }
  },

  // ================= DECISION PRESET METHODS =================

  /**
   * Get all org-wide decision presets
   */
  getPresets: async () => {
    try {
      const response = await api.get("/api/decisions/presets");
      return response.data;
    } catch (error) {
      console.error("Error fetching presets:", error);
      throw error;
    }
  },

  /**
   * Create a new decision preset
   * @param {Object} data - { label, color, bg, autoArchive }
   */
  createPreset: async (data) => {
    try {
      const response = await api.post("/api/decisions/presets", data);
      return response.data;
    } catch (error) {
      console.error("Error creating preset:", error);
      throw error;
    }
  },

  /**
   * Update a decision preset
   * @param {string} id - Preset ID
   * @param {Object} data - Fields to update
   */
  updatePreset: async (id, data) => {
    try {
      const response = await api.put(`/api/decisions/presets/${id}`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating preset:", error);
      throw error;
    }
  },

  /**
   * Delete a decision preset
   * @param {string} id - Preset ID
   */
  deletePreset: async (id) => {
    try {
      const response = await api.delete(`/api/decisions/presets/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting preset:", error);
      throw error;
    }
  },

  // ================= PASS REASON METHODS =================

  getPassReasons: async () => {
    try {
      const response = await api.get("/api/pass-reasons");
      return response.data;
    } catch (error) {
      console.error("Error fetching pass reasons:", error);
      throw error;
    }
  },

  createPassReason: async (data) => {
    try {
      const response = await api.post("/api/pass-reasons", data);
      return response.data;
    } catch (error) {
      console.error("Error creating pass reason:", error);
      throw error;
    }
  },

  deletePassReason: async (id) => {
    try {
      const response = await api.delete(`/api/pass-reasons/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting pass reason:", error);
      throw error;
    }
  },

  // Get top RFPs from new backend
  getTopRfps: async (limit = 10) => {
    try {
      const response = await api.get(`/api/top5-rfps/top-rfps?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error("Error getting top RFPs:", error);
      return { success: false, topRFPs: [], count: 0 };
    }
  },
  // apiService.js - Add this function
  getReport: async (opportunityId) => {
    try {
      const response = await api.get(`/api/reports/${opportunityId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting report:", error);
      return { success: false, error: "Failed to fetch report" };
    }
  },
  // Get user's feedback status for specific RFPs
  getUserFeedbackStatus: async (userEmail, solicitationNumbers = []) => {
    try {
      const params = new URLSearchParams();
      if (userEmail) params.append("email", userEmail);
      if (solicitationNumbers.length > 0) {
        params.append("solicitationNumbers", solicitationNumbers.join(","));
      }

      const response = await api.get(`/api/feedback/user-status?${params}`);
      return response.data;
    } catch (error) {
      console.error("Error getting user feedback status:", error);
      return { success: false, feedbackStatus: {} };
    }
  },

  // New feedback endpoints
  submitFeedbackNew: async (
    solicitationNumber,
    feedbackType,
    opportunityId,
    noticeId,
    opportunityData,
    userId,
    email,
    companyId,
    userFeedback = "",
    source = "",
    isUpdate = false,
    isGoodMatch,
    willPursue,
    passReasons,
  ) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/feedback/submit`, {
        solicitationNumber,
        feedbackType,
        userNotes: "",
        opportunityData,
        opportunityId,
        noticeId,
        userId,
        email,
        companyId,
        userFeedback,
        source,
        isUpdate,
        isGoodMatch,
        willPursue,
        passReasons,
      });
      return response.data;
    } catch (error) {
      console.error("Error submitting feedback:", error);
      throw error;
    }
  },
  getFeedbackStats: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value);
        }
      });

      const queryString = params.toString();
      const url = queryString
        ? `/api/feedback/stats?${queryString}`
        : "/api/feedback/stats";

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("Error getting feedback stats:", error);
      return { success: false, stats: {} };
    }
  },

  getFeedbackData: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value);
        }
      });

      const queryString = params.toString();
      const url = queryString
        ? `/api/feedback/data?${queryString}`
        : "/api/feedback/data";

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("Error getting feedback data:", error);
      return { success: false, data: [], pagination: null };
    }
  },

  getLearnedFilters: async () => {
    try {
      const response = await api.get("/api/feedback/filters");
      return response.data;
    } catch (error) {
      console.error("Error getting learned filters:", error);
      return { success: false, filters: null };
    }
  },

  triggerLearning: async () => {
    try {
      const response = await api.post("/api/cron/trigger/learning");
      return response.data;
    } catch (error) {
      console.error("Error triggering learning:", error);
      throw error;
    }
  },
  getSubscriptionStatus: async () => {
    try {
      const response = await api.get("/api/billing/subscription");
      return response.data;
    } catch (error) {
      console.error("Error fetching subscription status:", error);
      return {
        success: false,
        data: {
          planType: "FREE",
          canCustomize: false,
          isFreePlan: true,
        },
      };
    }
  },
  // Publish/revert top RFPs
  publishTopRfps: async (action) => {
    try {
      const response = await api.post("/api/publish-top-rfps", { action });
      return response.data;
    } catch (error) {
      console.error("Error publishing top RFPs:", error);
      throw error;
    }
  },

  // Company data endpoints
  getCompanyData: async (company) => {
    try {
      const response = await api.get(`/api/company/${company}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting ${company} data:`, error);
      throw error;
    }
  },

  // Manual scoring trigger
  triggerScoring: async () => {
    try {
      const response = await api.post("/api/cron/trigger/scoring");
      return response.data;
    } catch (error) {
      console.error("Error triggering scoring:", error);
      throw error;
    }
  },

  // Manual scraper trigger
  triggerScraper: async () => {
    try {
      const response = await api.post("/api/scraping/scrape");
      return response.data;
    } catch (error) {
      console.error("Error triggering scraper:", error);
      throw error;
    }
  },

  // Get scraping statistics
  getScrapingStats: async () => {
    try {
      const response = await api.get("/api/scraping/stats");
      return response.data;
    } catch (error) {
      console.error("Error getting scraping stats:", error);
      return { success: false, stats: {} };
    }
  },

  // Get today's opportunities (we'll calculate unscored from this)
  getTodayOpportunities: async () => {
    try {
      const response = await api.get(
        "/api/scraping/recent?limit=100&today_only=true&min_score=0",
      );
      return response.data;
    } catch (error) {
      console.error("Error getting today's opportunities:", error);
      return { success: false, opportunities: [], count: 0 };
    }
  },

  // Get recent opportunities
  getRecentOpportunities: async (limit = 20, minScore = 0) => {
    try {
      const response = await api.get(
        `/api/scraping/recent?limit=${limit}&min_score=${minScore}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting recent opportunities:", error);
      return { success: false, opportunities: [], count: 0 };
    }
  },

  // Score top 5 RFPs
  scoreTop5: async () => {
    try {
      const response = await api.post("/api/scraping/score-top5");
      return response.data;
    } catch (error) {
      console.error("Error scoring top 5:", error);
      throw error;
    }
  },

  // Search RFPs
  searchRfps: async (query, filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (query) params.append("query", query);
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/api/rfps/search?${params}`);
      return response.data;
    } catch (error) {
      console.error("Error searching RFPs:", error);
      throw error;
    }
  },

  // Get RFP details
  // Add this method to your apiService object
  getRfpDetails: async (id) => {
    try {
      const response = await api.get(`/api/top5-rfps/${id}/details`);
      return response.data;
    } catch (error) {
      console.error("Error getting RFP details:", error);
      throw error;
    }
  },

  // Admin endpoints
  getDashboardData: async () => {
    try {
      const response = await api.get("/api/admin/dashboard");
      return response.data;
    } catch (error) {
      console.error("Error getting dashboard data:", error);
      throw error;
    }
  },

  // Additional APIs from authentic-sam-server.js
  getAllScoredRfps: async (page = 1, limit = 50) => {
    try {
      const response = await api.get(
        `/api/all-scored-rfps?page=${page}&limit=${limit}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting scored RFPs:", error);
      throw error;
    }
  },

  analyzeSimilarity: async (opportunity) => {
    try {
      const response = await api.post("/api/analyze-similarity", {
        opportunity,
      });
      return response.data;
    } catch (error) {
      console.error("Error analyzing similarity:", error);
      throw error;
    }
  },

  searchRfp: async (rfpNumber, rfpType = null) => {
    try {
      const response = await api.post("/api/search-rfp", {
        rfpNumber,
        rfpType,
      });
      return response.data;
    } catch (error) {
      console.error("Error searching RFP:", error);
      throw error;
    }
  },

  testSamApi: async () => {
    try {
      const response = await api.post("/api/test-sam-api");
      return response.data;
    } catch (error) {
      console.error("Error testing SAM API:", error);
      throw error;
    }
  },

  submitFeedback: async (feedbackData) => {
    try {
      const response = await api.post("/api/feedback", feedbackData);
      return response.data;
    } catch (error) {
      console.error("Error submitting feedback:", error);
      throw error;
    }
  },

  getJobStatus: async (jobId) => {
    try {
      const response = await api.get(`/api/cron/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting job status for ${jobId}:`, error);
      throw error;
    }
  },

  // ===== User Preferences API =====

  /**
   * Get user preferences (keywords, departments, NAICS codes, avoid keywords, and scoring weights)
   * @returns {Promise<Object>} User preferences object
   * @example
   * {
   *   keywords: ["software", "cloud", "AI"],
   *   departments: ["Defense", "Energy"],
   *   naicsCodes: ["541511", "541512"],
   *   avoidKeywords: ["construction", "janitorial"],
   *   keywordScore: 40,
   *   naicsCodeScore: 20,
   *   departmentScore: 15,
   *   assessmentScore: 10,
   *   qualityScore: 15,
   *   isPreferencesUpdated: false
   * }
   */
  getUserPreferences: async () => {
    try {
      const response = await api.get("/api/preferences/user");
      return response.data;
    } catch (error) {
      console.error("Error getting user preferences:", error);
      throw error;
    }
  },

  /**
   * Update user preferences
   * @param {Object} preferences - Preferences object
   * @param {Array<string>} preferences.keywords - Target keywords
   * @param {Array<string>} preferences.departments - Preferred departments
   * @param {Array<string>} preferences.naicsCodes - NAICS codes
   * @param {Array<string>} preferences.avoidKeywords - Keywords to exclude
   * @param {number} preferences.keywordScore - Keyword relevance weight (0-100)
   * @param {number} preferences.naicsCodeScore - NAICS code alignment weight (0-100)
   * @param {number} preferences.departmentScore - Department preference weight (0-100)
   * @param {number} preferences.assessmentScore - Risk assessment weight (0-100)
   * @param {number} preferences.qualityScore - Opportunity quality weight (0-100)
   * @param {boolean} preferences.isPreferencesUpdated - Whether the preferences have been updated
   * @returns {Promise<Object>} Updated preferences
   * @note All score weights must sum to 100
   */
  updateUserPreferences: async (preferences) => {
    try {
      const response = await api.put("/api/preferences/user", preferences);
      return response.data;
    } catch (error) {
      console.error("Error updating user preferences:", error);
      throw error;
    }
  },

  /**
   * Get available options for preferences (all keywords, departments, NAICS codes)
   * This will be used to populate the dropdown options
   * @returns {Promise<Object>} Available options for each preference type
   */
  getPreferenceOptions: async () => {
    try {
      const response = await api.get("/api/preferences");
      return response.data;
    } catch (error) {
      console.error("Error getting preference options:", error);
      throw error;
    }
  },
  // ===== Groups API =====

  /**
   * Get user's groups
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search term for group name/description
   * @param {number} params.skip - Number of groups to skip (pagination)
   * @param {number} params.take - Number of groups to return (pagination)
   * @returns {Promise<Object>} Groups list
   */
  getUserGroups: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          queryParams.append(key, value);
        }
      });

      queryParams.append("_ts", Date.now());
      const queryString = queryParams.toString();
      const url = `/api/groups?${queryString}`;

      const response = await api.get(url);
      const responseData = response.data;

      // Handle the response data - prioritize items array
      let groups = [];

      if (responseData.items && Array.isArray(responseData.items)) {
        groups = responseData.items;
      } else if (responseData.groups && Array.isArray(responseData.groups)) {
        groups = responseData.groups;
      } else if (Array.isArray(responseData)) {
        groups = responseData;
      }

      return {
        success: true,
        groups: groups,
        total: responseData.total || groups.length,
      };
    } catch (error) {
      console.error("Error getting user groups:", error);
      return {
        success: false,
        groups: [],
        error: error.message,
      };
    }
  },

  /**
   * Create a new group
   * @param {Object} data - Group data
   * @param {string} data.name - Group name (required)
   * @param {string} data.description - Group description (optional)
   * @returns {Promise<Object>} Created group
   */
  // services/api.js
  createGroup: async (data) => {
    try {
      const response = await api.post("/api/groups", data);
      return response.data;
    } catch (error) {
      console.error("Error creating group:", error);
      // Don't return, throw the error instead
      throw error;
    }
  },

  /**
   * Create a group from a recipe (opportunity template).
   * The group is typed "Recipe" and its targeting document is seeded
   * from the recipe's NAICS codes, keywords and agencies.
   * @param {Object} data
   * @param {string} data.templateId - Recipe to build from (required)
   * @param {string} data.name - Group name (required)
   * @param {string} data.description - Group description (optional)
   * @returns {Promise<Object>} Created group
   */
  createGroupFromTemplate: async (data) => {
    const response = await api.post("/api/groups/from-template", data);
    return response.data;
  },

  updateGroup: async (groupId, data) => {
    try {
      const response = await api.put(`/api/groups/${groupId}`, data);
      return response.data;
    } catch (error) {
      console.error("Error updating group:", error);
      throw error;
    }
  },

  deleteGroup: async (groupId) => {
    try {
      const response = await api.delete(`/api/groups/${groupId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting group:", error);
      throw error;
    }
  },

  /**
   * Get specific group with RFPs
   * @param {string} groupId - Group ID
   * @returns {Promise<Object>} Group details with RFPs
   */
  getGroupWithRFPs: async (groupId) => {
    try {
      const response = await api.get(`/api/groups/${groupId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting group with RFPs:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Add RFP to group
   * @param {string} groupId - Group ID
   * @param {Object} data - RFP data
   * @param {number} data.opportunityId - SAM Opportunity ID
   * @returns {Promise<Object>} Added group RFP
   */
  addRFPToGroup: async (groupId, data) => {
    try {
      const response = await api.post(`/api/groups/${groupId}/rfps`, data);
      return response.data;
    } catch (error) {
      console.error("Error adding RFP to group:", error);
      return { success: false, error: error.message };
    }
  },
  // In your apiService, add this method
  getUserOwnedGroups: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          queryParams.append(key, value);
        }
      });

      queryParams.append("_ts", Date.now());
      const queryString = queryParams.toString();
      const url = `/api/groups/owned?${queryString}`;

      const response = await api.get(url);
      const responseData = response.data;

      let groups = [];

      if (responseData.items && Array.isArray(responseData.items)) {
        groups = responseData.items;
      } else if (responseData.groups && Array.isArray(responseData.groups)) {
        groups = responseData.groups;
      } else if (Array.isArray(responseData)) {
        groups = responseData;
      }

      return {
        success: true,
        groups: groups,
        total: responseData.total || groups.length,
      };
    } catch (error) {
      console.error("Error getting user owned groups:", error);
      return {
        success: false,
        groups: [],
        error: error.message,
      };
    }
  },
  /**
   * Get group RFPs
   * @param {string} groupId - Group ID
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of RFPs to skip
   * @param {number} params.take - Number of RFPs to return
   * @returns {Promise<Object>} Group RFPs list
   */
  getGroupRFPs: async (groupId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const url = queryString
        ? `/api/groups/${groupId}/rfps?${queryString}`
        : `/api/groups/${groupId}/rfps`;

      const response = await api.get(url);

      // Transform the response to match expected format - INCLUDING GROUP DATA
      return {
        success: true,
        items: response.data.items || [],
        total: response.data.total || 0,
        group: response.data.group || null, // ← Add this line!
      };
    } catch (error) {
      console.error("Error getting group RFPs:", error);
      return {
        success: false,
        error: error.message,
        items: [],
        total: 0,
        group: null,
      };
    }
  },
  /**
   * Remove RFP from group
   * @param {string} groupId - Group ID
   * @param {number} opportunityId - SAM Opportunity ID
   * @returns {Promise<Object>} Remove result
   */
  removeRFPFromGroup: async (groupId, opportunityId) => {
    try {
      const response = await api.delete(
        `/api/groups/${groupId}/rfps/${opportunityId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error removing RFP from group:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Check if RFP is in group
   * @param {string} groupId - Group ID
   * @param {number} opportunityId - SAM Opportunity ID
   * @returns {Promise<Object>} Check result
   */
  checkRFPInGroup: async (groupId, opportunityId) => {
    try {
      const response = await api.get(
        `/api/groups/${groupId}/rfps/check/${opportunityId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error checking RFP in group:", error);

      return {
        exists: false,
        error: error.message,
        ...error.response?.data,
      };
    }
  },

  // Group Members API methods
  getGroupMembers: async (groupId) => {
    try {
      const response = await api.get(`/api/groups/${groupId}/members`);
      return response.data;
    } catch (error) {
      console.error("Error getting group members:", error);
      throw error;
    }
  },

  addGroupMembers: async (groupId, userIds) => {
    try {
      const response = await api.post(`/api/groups/${groupId}/members`, {
        userIds,
      });
      return response.data;
    } catch (error) {
      console.error("Error adding group members:", error);
      throw error;
    }
  },

  removeGroupMember: async (groupId, memberId) => {
    try {
      const response = await api.delete(
        `/api/groups/${groupId}/members/${memberId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error removing group member:", error);
      throw error;
    }
  },

  getAvailableUsers: async (groupId, search = "") => {
    try {
      const queryParams = search ? `?search=${encodeURIComponent(search)}` : "";
      const response = await api.get(
        `/api/groups/${groupId}/available-users${queryParams}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting available users:", error);
      throw error;
    }
  },
  // Add these to your apiService object in api.service.ts

  // Group Documents API endpoints
  getGroupDocuments: async (groupId) => {
    try {
      const response = await api.get(`/api/groups/${groupId}/documents`);
      return response.data;
    } catch (error) {
      console.error("Error getting group documents:", error);
      throw error;
    }
  },

  getAllUserDocuments: async () => {
    try {
      const response = await api.get("/api/groups/documents/all");
      return response.data;
    } catch (error) {
      console.error("Error getting all user documents:", error);
      throw error;
    }
  },

  getDocumentById: async (documentId) => {
    try {
      const response = await api.get(`/api/groups/documents/${documentId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting document:", error);
      throw error;
    }
  },

  uploadGroupDocument: async (groupId, formData) => {
    try {
      const response = await api.post(
        `/api/groups/${groupId}/documents`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading document:", error);
      throw error;
    }
  },

  updateGroupDocument: async (documentId, data) => {
    try {
      const response = await api.put(
        `/api/groups/documents/${documentId}`,
        typeof data === "string" ? { name: data } : data,
      );
      return response.data;
    } catch (error) {
      console.error("Error updating document:", error);
      throw error;
    }
  },

  deactivateGroupDocument: async (documentId) => {
    try {
      const response = await api.patch(`/api/groups/documents/${documentId}/deactivate`);
      return response.data;
    } catch (error) {
      console.error("Error deactivating document:", error);
      throw error;
    }
  },

  activateGroupDocument: async (documentId) => {
    try {
      const response = await api.patch(`/api/groups/documents/${documentId}/activate`);
      return response.data;
    } catch (error) {
      console.error("Error activating document:", error);
      throw error;
    }
  },

  deleteGroupDocument: async (documentId) => {
    try {
      const response = await api.delete(`/api/groups/documents/${documentId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting document:", error);
      throw error;
    }
  },

  downloadGroupDocument: async (documentId) => {
    try {
      const response = await api.get(
        `/api/groups/documents/${documentId}/download`,
        {
          responseType: "blob",
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error downloading document:", error);
      throw error;
    }
  },
  getGroupOpportunities: async (groupId) => {
    try {
      const response = await api.get(`/api/groups/${groupId}/opportunities`);
      return response.data;
    } catch (error) {
      console.error("Error fetching group opportunities:", error);
      throw error;
    }
  },

  getGroupOpportunityCounts: async (groupId) => {
    try {
      const response = await api.get(
        `/api/groups/${groupId}/opportunities/count`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching opportunity counts:", error);
      throw error;
    }
  },

  getGroupPipelineStats: async (groupId) => {
    try {
      const response = await api.get(`/api/groups/${groupId}/pipeline-stats`);
      return response.data;
    } catch (error) {
      console.error("Error fetching group pipeline stats:", error);
      throw error;
    }
  },

  searchOpportunitiesForDocument: async (documentId) => {
    try {
      const response = await api.post(
        `/api/groups/documents/${documentId}/search-opportunities`,
      );
      return response.data;
    } catch (error) {
      console.error("Error searching opportunities for document:", error);
      throw error;
    }
  },

  processOpportunitiesEmbedding: async () => {
    try {
      const response = await api.post(
        "/api/groups/admin/process-opportunities",
      );
      return response.data;
    } catch (error) {
      console.error("Error processing opportunities embedding:", error);
      throw error;
    }
  },
  getEmailPreference: async () => {
    try {
      const response = await api.get("/api/users/email-preference");
      return response.data;
    } catch (error) {
      console.error("Error getting email preference:", error);
      // Return default preferences on error
      return {
        success: false,
        data: { emailEnabled: true, emailDailyOnly: true }, // Defaults
      };
    }
  },

  updateEmailPreference: async (payload) => {
    try {
      const response = await api.put("/api/users/email-preference", payload);
      return response.data;
    } catch (error) {
      console.error("Error updating email preference:", error);
      throw error;
    }
  },

  getOtherGroups: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          queryParams.append(key, value);
        }
      });

      queryParams.append("_ts", Date.now());
      const queryString = queryParams.toString();

      const url = `/api/groups/other/list?${queryString}`;

      const response = await api.get(url);
      const responseData = response.data;

      let groups = [];

      if (responseData.items && Array.isArray(responseData.items)) {
        groups = responseData.items;
      } else if (responseData.groups && Array.isArray(responseData.groups)) {
        groups = responseData.groups;
      } else if (Array.isArray(responseData)) {
        groups = responseData;
      }

      return {
        success: true,
        groups: groups,
        total: responseData.total || groups.length,
      };
    } catch (error) {
      console.error("Error getting other groups:", error);
      return {
        success: false,
        groups: [],
        error: error.message,
      };
    }
  },

  /**
   * Request to join a group
   */
  requestToJoinGroup: async (groupId) => {
    try {
      const response = await api.post(`/api/groups/${groupId}/request-join`);
      return response.data;
    } catch (error) {
      console.error("Error requesting to join group:", error);
      throw error;
    }
  },

  /**
   * Get pending requests for a group (admin/owner only)
   */
  getGroupJoinRequests: async (groupId) => {
    try {
      const response = await api.get(`/api/groups/${groupId}/requests`);
      return response.data;
    } catch (error) {
      console.error("Error getting group requests:", error);
      throw error;
    }
  },

  cancelJoinRequest: async (requestId) => {
    try {
      const response = await api.delete(`/api/groups/requests/${requestId}`);
      return response.data;
    } catch (error) {
      console.error("Error canceling join request:", error);
      throw error;
    }
  },

  acceptJoinRequest: async (requestId) => {
    try {
      const response = await api.post(
        `/api/groups/requests/${requestId}/accept`,
      );
      return response.data;
    } catch (error) {
      console.error("Error accepting join request:", error);
      throw error;
    }
  },

  rejectJoinRequest: async (requestId) => {
    try {
      const response = await api.post(
        `/api/groups/requests/${requestId}/reject`,
      );
      return response.data;
    } catch (error) {
      console.error("Error rejecting join request:", error);
      throw error;
    }
  },
  leaveGroup: async (groupId) => {
    try {
      const response = await api.delete(`/api/groups/${groupId}/leave`);
      return response.data;
    } catch (error) {
      console.error("Error leaving group:", error);
      throw error;
    }
  },
  /**
   * Get pending groups for approval (admin only)
   */
  getPendingGroups: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const url = queryString
        ? `/api/groups/pending?${queryString}`
        : `/api/groups/pending`;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("Error getting pending groups:", error);
      throw error;
    }
  },

  /**
   * Approve a group (admin only)
   */
  approveGroup: async (groupId) => {
    try {
      const response = await api.post(`/api/groups/${groupId}/approve`);
      return response.data;
    } catch (error) {
      console.error("Error approving group:", error);
      throw error;
    }
  },

  /**
   * Reject a group (admin only)
   */
  rejectGroup: async (groupId, reason) => {
    try {
      const response = await api.post(`/api/groups/${groupId}/reject`, {
        reason,
      });
      return response.data;
    } catch (error) {
      console.error("Error rejecting group:", error);
      throw error;
    }
  },
  // In your apiService.js
  getMyGroups: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          queryParams.append(key, value);
        }
      });

      const queryString = queryParams.toString();
      const url = queryString
        ? `/api/groups/my-groups?${queryString}`
        : `/api/groups/my-groups`;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error("Error getting my groups:", error);
      throw error;
    }
  },

  checkRfpSharedStatus: async (rfpId, data) => {
    try {
      const response = await api.post(
        `/api/top5-rfps/${rfpId}/shared-status`,
        data,
      );
      return response.data;
    } catch (error) {
      console.error("Error checking RFP shared status:", error);
      throw error;
    }
  },

  // Fix 2: getTeamMembers - Use api instance instead of axios
  getTeamMembers: async (params) => {
    try {
      const response = await api.get("/api/users", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching team members:", error);
      throw error;
    }
  },

  shareRfp: async (data) => {
    try {
      const response = await api.post("/api/top5-rfps/share-rfp", data);
      return response.data;
    } catch (error) {
      console.error("Error sharing RFP:", error);

      return {
        success: false,
        error:
          error.response?.data?.error || error.message || "Failed to share RFP",
        status: error.response?.status || 500,
      };
    }
  },

  // ==================== ARCHIVE METHODS ====================

  /**
   * Manually archive an opportunity
   * @param {string|number} opportunityId - The ID of the opportunity to archive
   * @returns {Promise<Object>} Archive result
   */
  async archiveOpportunity(opportunityId) {
    try {
      const response = await api.post(
        `/api/archive/manual/${opportunityId}`,
        {},
      );
      return response.data;
    } catch (error) {
      console.error("Error archiving opportunity:", error);
      // Return structured error for better handling
      return {
        success: false,
        message:
          error.response?.data?.message || "Failed to archive opportunity",
        error: error.message,
      };
    }
  },

  /**
   * Unarchive an opportunity
   * @param {string|number} opportunityId - The ID of the opportunity to unarchive
   * @returns {Promise<Object>} Unarchive result
   */
  async unarchiveOpportunity(opportunityId) {
    try {
      const response = await api.post(
        `/api/archive/unarchive/${opportunityId}`,
        {},
      );
      return response.data;
    } catch (error) {
      console.error("Error unarchiving opportunity:", error);
      return {
        success: false,
        message:
          error.response?.data?.message || "Failed to unarchive opportunity",
        error: error.message,
      };
    }
  },

  /**
   * Get archived opportunities with pagination and sorting
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 25)
   * @param {string} params.sortBy - Sort field (archivedAt, archivedReason, archivedByName)
   * @param {string} params.sortOrder - Sort order (asc, desc)
   * @returns {Promise<Object>} Archived opportunities with pagination
   */
  async getArchivedOpportunities(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Set defaults if not provided
      const page = params.page || 1;
      const limit = params.limit || 25;
      const sortBy = params.sortBy || "archivedAt";
      const sortOrder = params.sortOrder || "desc";

      queryParams.append("page", page);
      queryParams.append("limit", limit);
      queryParams.append("sortBy", sortBy);
      queryParams.append("sortOrder", sortOrder);

      const response = await api.get(
        `/api/archive/list?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching archived opportunities:", error);
      return {
        success: false,
        archives: [],
        pagination: {
          currentPage: params.page || 1,
          totalPages: 1,
          totalCount: 0,
          limit: params.limit || 25,
          hasNextPage: false,
          hasPrevPage: false,
        },
        message:
          error.response?.data?.message ||
          "Failed to fetch archived opportunities",
      };
    }
  },

  /**
   * Get count of archived opportunities
   * @returns {Promise<Object>} Archive count result
   */
  async getArchiveCount() {
    try {
      const response = await api.get("/api/archive/count");
      return response.data;
    } catch (error) {
      console.error("Error fetching archive count:", error);
      return {
        success: false,
        count: 0,
        message:
          error.response?.data?.message || "Failed to fetch archive count",
      };
    }
  },

  /**
   * Check if an opportunity is archived
   * @param {string|number} opportunityId - The ID of the opportunity to check
   * @returns {Promise<Object>} Archive status result
   */
  async checkArchiveStatus(opportunityId) {
    try {
      const response = await api.get(`/api/archive/status/${opportunityId}`);
      return response.data;
    } catch (error) {
      console.error("Error checking archive status:", error);
      return {
        success: false,
        isArchived: false,
        message:
          error.response?.data?.message || "Failed to check archive status",
      };
    }
  },

  // ==================== GROUP ARCHIVE METHODS ====================

  /**
   * Manually archive an opportunity within a specific group
   * @param {string} groupId - The group ID
   * @param {string|number} opportunityId - The opportunity ID
   * @param {Object} body - Optional body (e.g. { relevantDocumentId })
   */
  async archiveOpportunityInGroup(groupId, opportunityId, body = {}) {
    try {
      const response = await api.post(
        `/api/group-archive/${groupId}/archive/${opportunityId}`,
        body,
      );
      return response.data;
    } catch (error) {
      console.error("Error archiving opportunity in group:", error);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Failed to archive opportunity in group",
        error: error.message,
      };
    }
  },

  /**
   * Unarchive an opportunity within a specific group
   * @param {string} groupId - The group ID
   * @param {string|number} opportunityId - The opportunity ID
   */
  async unarchiveOpportunityInGroup(groupId, opportunityId, body = {}) {
    try {
      const response = await api.post(
        `/api/group-archive/${groupId}/unarchive/${opportunityId}`,
        body,
      );
      return response.data;
    } catch (error) {
      console.error("Error unarchiving opportunity in group:", error);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Failed to unarchive opportunity in group",
        error: error.message,
      };
    }
  },

  /**
   * Get archived opportunities for a specific group
   * @param {string} groupId - The group ID
   * @param {Object} params - Query params (page, limit, sortBy, sortOrder)
   */
  async getGroupArchived(groupId, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", params.page || 1);
      queryParams.append("limit", params.limit || 25);
      queryParams.append("sortBy", params.sortBy || "archivedAt");
      queryParams.append("sortOrder", params.sortOrder || "desc");

      const response = await api.get(
        `/api/group-archive/${groupId}/archived?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching group archived opportunities:", error);
      return {
        success: false,
        archives: [],
        message:
          error.response?.data?.message ||
          "Failed to fetch group archived opportunities",
      };
    }
  },

  /**
   * Get archive count for a specific group
   * @param {string} groupId - The group ID
   */
  async getGroupArchiveCount(groupId) {
    try {
      const response = await api.get(
        `/api/group-archive/${groupId}/archive-count`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching group archive count:", error);
      return {
        success: false,
        count: 0,
        message:
          error.response?.data?.message ||
          "Failed to fetch group archive count",
      };
    }
  },
  // Add these two methods to the apiService object,
  // alongside the other group methods (e.g. after getMyGroups)

  getGroupsForMove: async (search = "") => {
    try {
      const params = search.trim()
        ? `?search=${encodeURIComponent(search.trim())}`
        : "";
      const response = await api.get(`/api/groups/move-targets${params}`);
      return response.data;
    } catch (error) {
      console.error("Error getting groups for move:", error);
      return { success: false, groups: [] };
    }
  },

  moveOpportunityToGroup: async (groupId, opportunityId, note = "") => {
    try {
      const response = await api.post(
        `/api/groups/${groupId}/opportunities/move`,
        { opportunityId, note: note || undefined },
      );
      return response.data;
    } catch (error) {
      console.error("Error moving opportunity to group:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to move opportunity",
      };
    }
  },

  getGroupTargetingDocument: async (groupId, documentId) => {
    try {
      const params = documentId ? { documentId } : {};
      const response = await api.get(
        `/api/groups/${groupId}/targeting-document`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error("Error getting targeting document:", error);
      throw error;
    }
  },

  seedGroupTargetingDocument: async (groupId, formData) => {
    try {
      const response = await api.post(
        `/api/groups/${groupId}/targeting-document/seed`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data;
    } catch (error) {
      console.error("Error seeding targeting document:", error);
      throw error;
    }
  },

  saveGroupTargetingDocument: async (groupId, content, groupDocumentId, pendingFile) => {
    try {
      const response = await api.put(
        `/api/groups/${groupId}/targeting-document`,
        { content, groupDocumentId, pendingFile: pendingFile || undefined },
      );
      return response.data;
    } catch (error) {
      console.error("Error saving targeting document:", error);
      throw error;
    }
  },

  getTargetingDocumentVersions: async (groupId, documentId) => {
    try {
      const params = documentId ? { documentId } : {};
      const response = await api.get(
        `/api/groups/${groupId}/targeting-document/versions`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching targeting document versions:", error);
      throw error;
    }
  },

  getTargetingDocumentChatHistory: async (groupId) => {
    try {
      const response = await api.get(
        `/api/groups/${groupId}/targeting-document/chat`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching targeting document chat history:", error);
      throw error;
    }
  },

  sendTargetingDocumentChatMessage: async (groupId, message, groupDocumentId) => {
    try {
      const response = await api.post(
        `/api/groups/${groupId}/targeting-document/chat`,
        { message, ...(groupDocumentId ? { groupDocumentId } : {}) },
      );
      return response.data;
    } catch (error) {
      console.error("Error sending targeting document chat message:", error);
      throw error;
    }
  },

  clearTargetingDocumentChat: async (groupId) => {
    try {
      const response = await api.delete(
        `/api/groups/${groupId}/targeting-document/chat`,
      );
      return response.data;
    } catch (error) {
      console.error("Error clearing targeting document chat:", error);
      throw error;
    }
  },

  markTargetingDocumentChatApplied: async (groupId, messageId) => {
    try {
      const response = await api.post(
        `/api/groups/${groupId}/targeting-document/chat/${messageId}/applied`,
      );
      return response.data;
    } catch (error) {
      console.error("Error marking chat message applied:", error);
      throw error;
    }
  },

  getTargetingDocumentVersionContent: async (groupId, versionId) => {
    try {
      const response = await api.get(
        `/api/groups/${groupId}/targeting-document/versions/${versionId}/content`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching version content:", error);
      throw error;
    }
  },

  deactivateTargetingVersion: async (groupId, versionId) => {
    try {
      const response = await api.patch(
        `/api/groups/${groupId}/targeting-document/versions/${versionId}/deactivate`,
      );
      return response.data;
    } catch (error) {
      console.error("Error deactivating targeting version:", error);
      throw error;
    }
  },

  updateTargetingDocumentTitle: async (groupId, title, documentId) => {
    try {
      const response = await api.patch(
        `/api/groups/${groupId}/targeting-document/title`,
        { title, documentId },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating targeting document title:", error);
      throw error;
    }
  },

  createTargetingDocumentDraft: async (groupId, { mode, content, url, file, groupDocumentId } = {}) => {
    try {
      const formData = new FormData();
      formData.append("mode", mode);
      if (content) formData.append("content", content);
      if (url) formData.append("url", url);
      if (file) formData.append("file", file);
      if (groupDocumentId) formData.append("groupDocumentId", groupDocumentId);
      const response = await api.post(
        `/api/groups/${groupId}/targeting-document/draft`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data;
    } catch (error) {
      console.error("Error creating targeting document draft:", error);
      throw error;
    }
  },

  saveTargetingDocumentDraftContent: async (groupId, content, groupDocumentId) => {
    try {
      const response = await api.patch(
        `/api/groups/${groupId}/targeting-document/draft`,
        { content, ...(groupDocumentId ? { groupDocumentId } : {}) },
      );
      return response.data;
    } catch (error) {
      console.error("Error saving targeting document draft content:", error);
      throw error;
    }
  },

  goLiveTargetingDocumentDraft: async (groupId, groupDocumentId) => {
    try {
      const response = await api.post(
        `/api/groups/${groupId}/targeting-document/draft/go-live`,
        groupDocumentId ? { groupDocumentId } : {},
      );
      return response.data;
    } catch (error) {
      console.error("Error going live with targeting document draft:", error);
      throw error;
    }
  },

  discardTargetingDocumentDraft: async (groupId) => {
    const response = await api.delete(`/api/groups/${groupId}/targeting-document/draft`);
    return response.data;
  },

  restoreTargetingDocumentVersion: async (groupId, versionId) => {
    try {
      const response = await api.post(
        `/api/groups/${groupId}/targeting-document/versions/${versionId}/restore`,
      );
      return response.data;
    } catch (error) {
      console.error("Error restoring targeting document version:", error);
      throw error;
    }
  },

  confirmTargetingRule: async (groupId, messageId, decision, groupDocumentId) => {
    try {
      const response = await api.post(
        `/api/groups/${groupId}/targeting-document/chat/${messageId}/confirm-rule`,
        { decision, ...(groupDocumentId ? { groupDocumentId } : {}) },
      );
      return response.data;
    } catch (error) {
      console.error("Error confirming targeting rule:", error);
      throw error;
    }
  },

  getTargetingRules: async (groupId, groupDocumentId) => {
    try {
      const response = await api.get(
        `/api/groups/${groupId}/targeting-document/rules`,
        { params: groupDocumentId ? { groupDocumentId } : {} },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching targeting rules:", error);
      throw error;
    }
  },

  createTargetingRule: async (groupId, payload) => {
    try {
      const response = await api.post(
        `/api/groups/${groupId}/targeting-document/rules`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("Error creating targeting rule:", error);
      throw error;
    }
  },

  updateTargetingRuleMode: async (groupId, ruleId, mode) => {
    try {
      const response = await api.patch(
        `/api/groups/${groupId}/targeting-document/rules/${ruleId}`,
        { mode },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating targeting rule:", error);
      throw error;
    }
  },

  deleteTargetingRule: async (groupId, ruleId) => {
    try {
      const response = await api.delete(
        `/api/groups/${groupId}/targeting-document/rules/${ruleId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting targeting rule:", error);
      throw error;
    }
  },

  proposeTargetingRuleFromOpportunity: async (groupId, opportunityId) => {
    try {
      const response = await api.post(
        `/api/groups/${groupId}/targeting-document/rules/from-opportunity`,
        { opportunityId },
      );
      return response.data;
    } catch (error) {
      console.error("Error building rule proposal:", error);
      throw error;
    }
  },

  // ALX-209 — feedback-driven targeting change
  getMatchedGroups: async (opportunityId) => {
    try {
      const response = await api.get("/api/feedback/matched-groups", {
        params: { opportunityId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching matched groups:", error);
      throw error;
    }
  },

  proposeTargetingRuleFromNote: async (groupId, { note, reasonChips, opportunityId }) => {
    try {
      const response = await api.post(
        `/api/groups/${groupId}/targeting-document/rules/propose-from-note`,
        { note, reasonChips, opportunityId },
      );
      return response.data;
    } catch (error) {
      console.error("Error proposing rule from note:", error);
      throw error;
    }
  },

  applyTargetingRuleFromFeedback: async (groupId, { proposedRule, note, opportunityId }) => {
    try {
      const response = await api.post(
        `/api/groups/${groupId}/targeting-document/rules/apply-from-feedback`,
        { proposedRule, note, opportunityId },
      );
      return response.data;
    } catch (error) {
      console.error("Error applying feedback rule:", error);
      throw error;
    }
  },

  getGroupVoteFeedback: async (groupId) => {
    try {
      const response = await api.get(`/api/groups/${groupId}/vote-feedback`);
      return response.data;
    } catch (error) {
      console.error("Error fetching group vote feedback:", error);
      throw error;
    }
  },

  getMatchAnalysis: async (groupId, opportunityId) => {
    try {
      const response = await api.get(
        `/api/groups/${groupId}/opportunities/${opportunityId}/match-analysis`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching match analysis:", error);
      throw error;
    }
  },

  getExternalPartners: async () => {
    try {
      const response = await api.get("/api/external-orgs/connections");
      return response.data?.data ?? [];
    } catch (error) {
      console.error("Error fetching external partners:", error);
      throw error;
    }
  },

  getExternalPendingInvites: async () => {
    try {
      const response = await api.get("/api/external-orgs/pending");
      return response.data?.data ?? [];
    } catch (error) {
      console.error("Error fetching external pending invites:", error);
      throw error;
    }
  },

  sendExternalOrgInvite: async (data) => {
    try {
      const response = await api.post("/api/external-orgs/invite", data);
      return response.data;
    } catch (error) {
      console.error("Error sending external org invite:", error);
      throw error;
    }
  },

  getIncomingExternalOrgInvites: async () => {
    try {
      const response = await api.get("/api/external-orgs/incoming");
      return response.data?.data ?? [];
    } catch (error) {
      console.error("Error fetching incoming external org invites:", error);
      throw error;
    }
  },

  acceptExternalOrgInvite: async (id) => {
    try {
      const response = await api.post(`/api/external-orgs/accept/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error accepting external org invite:", error);
      throw error;
    }
  },

  resendExternalOrgInvite: async (id) => {
    try {
      const response = await api.post(`/api/external-orgs/resend/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error resending external org invite:", error);
      throw error;
    }
  },

  cancelExternalOrgInvite: async (id) => {
    try {
      const response = await api.delete(`/api/external-orgs/cancel/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error cancelling external org invite:", error);
      throw error;
    }
  },

  disconnectExternalOrg: async (connectionId) => {
    try {
      const response = await api.delete(
        `/api/external-orgs/connections/${connectionId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error disconnecting external org:", error);
      throw error;
    }
  },

  getConnectionGroups: async (connectionId) => {
    const response = await api.get(`/api/external-orgs/connections/${connectionId}/groups`);
    return response.data?.data ?? [];
  },

  shareGroupWithConnection: async (connectionId, groupId) => {
    const response = await api.post(`/api/external-orgs/connections/${connectionId}/groups`, { groupId });
    return response.data;
  },

  unshareGroupFromConnection: async (connectionId, groupId) => {
    const response = await api.delete(`/api/external-orgs/connections/${connectionId}/groups/${groupId}`);
    return response.data;
  },
};

export default api;
